














































import { computed, defineComponent, PropType, Ref, ref } from '@nuxtjs/composition-api';
import clickOutside from 'vue-click-outside';

import useFuzzySearch from './useFuzzySearch';

interface IVehicleSelectionOption {
  title: string;
  value: string;
}

interface ISelection {
  isDisabled: boolean;
  label: string;
  options: IVehicleSelectionOption[];
  queryField: string;
  title: string;
}

const normalizeString = (str: string): string => {
  // Lowercase, remove spaces, dashes, and other non-alphanumeric characters
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
};

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Selection',
  directives: {
    clickOutside,
  },
  props: {
    selection: {
      type: Object as PropType<ISelection>,
      required: true,
    },
    currentSelections: {
      type: Array as PropType<IVehicleSelectionOption[]>,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    // Shows a down chevron so a filled-in field still reads as changeable.
    chevron: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props, { emit }) {
    const isOpen: Ref = ref(false);
    const hasFocus: Ref = ref(false);
    const searchText: Ref = ref('');
    const btnBorderClass: Ref = ref('selection-button-border');
    const inputBorder: Ref = ref('search-input-passive-border');

    const isOptionSelected = computed(
      () => !!props.currentSelections.find(({ title }) => title === props.selection.title),
    );

    const placeholderText = computed(() => props.placeholder || `Select ${props.selection.label}`);

    const selectedOption = computed(() => {
      const selectedOption = props.currentSelections.find(
        ({ title }) => title === props.selection.title,
      );
      return selectedOption ? selectedOption.value : '';
    });

    const selectionOptions = computed(() => props.selection.options);

    const { search } = useFuzzySearch(selectionOptions, {
      keys: ['title'],
      getFn: (options) => normalizeString(options.title),
    });

    const filterOptions = computed(() => {
      if (searchText.value) {
        return search(searchText.value);
      }
      return props.selection.options || [];
    });

    const onInputChange = (e: Event) => {
      const val = (e.target as HTMLInputElement).value;
      searchText.value = val;
      emit('text-change');
    };

    return {
      isOpen,
      searchText,
      hasFocus,
      btnBorderClass,
      inputBorder,
      isOptionSelected,
      selectedOption,
      filterOptions,
      placeholderText,
      onInputChange,
    };
  },
  watch: {
    isOpen(newVal) {
      this.btnBorderClass = newVal ? 'selection-button-focus-border' : 'selection-button-border';
    },
    currentSelections: {
      handler(newSelections) {
        const selected = newSelections.find(
          (s: IVehicleSelectionOption) => s.title === this.selection.title,
        );
        this.searchText = selected ? selected.value : '';
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyStroke);
  },
  destroyed() {
    // important to clean up!!
    window.removeEventListener('keydown', this.handleKeyStroke);
  },
  methods: {
    toggleSelections() {
      this.isOpen = !this.isOpen;
    },
    hide() {
      this.isOpen = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    focusParent() {
      this.inputBorder = 'search-input-active-border';
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    removeFocus() {
      this.inputBorder = 'search-input-passive-border';
    },
    handleKeyStroke(event: KeyboardEvent) {
      switch (event.key) {
        case 'Esc':
        case 'Escape':
          this.isOpen = false;
          break;
        default:
          break;
      }
    },
    optionSelected(event: MouseEvent) {
      const target = event.target as HTMLElement;
      this.isOpen = false;
      this.$emit('selected-option', {
        title: this.selection.title,
        value: target.id,
        queryField: this.selection.queryField,
      });

      this.searchText = target.id;
    },
  },
});
